// src/LoginPage.tsx

import React, { useState, useEffect } from 'react';
import { TextField, Container, Typography, Box, Tabs, Tab, useTheme, CircularProgress, InputAdornment, IconButton, Button, Dialog, DialogActions, DialogContent, DialogTitle, Alert } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { PrimaryButton, RegisterForm } from './components';
import { TipoUsuario } from './types/enums/TipoUsuario';
import SEO, { SEOConfigs } from './components/SEO';
import AuthService from './services/AuthService';

interface TabPanelProps {
  readonly children?: React.ReactNode;
  readonly index: number;
  readonly value: number;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const LoginPage: React.FC = () => {
  const authContext = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'cadastro' ? 1 : 0;
  const [tabValue, setTabValue] = useState(initialTab);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState<string | null>(null);

  // Atualiza a tab quando o parâmetro da URL mudar
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'cadastro') {
      setTabValue(1);
    }
  }, [searchParams]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

  const handleLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      const { needsPayment, needsSelectTenant } = await authContext.signIn(email, senha);

      if (needsSelectTenant) {
        navigate('/tw/selecao-empresa');
      } else if (needsPayment) {
        navigate('/tw/pagamento');
      } else {
        navigate('/tw/dashboard');
      }
    } catch (err: any) {
      setError(`${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordOpen = () => {
    setForgotPasswordEmail(email);
    setForgotPasswordError(null);
    setForgotPasswordSuccess(null);
    setForgotPasswordOpen(true);
  };

  const handleForgotPasswordClose = () => {
    if (forgotPasswordLoading) {
      return;
    }
    setForgotPasswordOpen(false);
  };

  const handleForgotPasswordSubmit = async () => {
    const normalizedEmail = forgotPasswordEmail.trim();
    if (!normalizedEmail) {
      setForgotPasswordError('Informe um e-mail válido.');
      return;
    }

    setForgotPasswordLoading(true);
    setForgotPasswordError(null);
    setForgotPasswordSuccess(null);

    try {
      const response = await AuthService.requestPasswordReset(normalizedEmail);
      setForgotPasswordSuccess(
        response.message || 'Se o e-mail existir em nossa base, enviaremos instruções para redefinir sua senha.'
      );
    } catch (err: any) {
      setForgotPasswordError(err.message);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleRegister = async (nome: string, email: string, senha: string, tipoUsuario: TipoUsuario, taxId: string, cellphone?: string) => {
    const result = await authContext.signUp(nome, email, senha, tipoUsuario, taxId, cellphone);
    
    // Se precisar de pagamento, redirecionar para a página de pagamento
    if (result.needsPayment) {
      navigate('/tw/pagamento');
    }
    
    return result;
  };

  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === "Enter") {
      event.preventDefault();
      action();
    }
  }

  return (
    <>
      <SEO {...SEOConfigs.login} />
      <Container
        maxWidth={false}
        sx={{
          minHeight: '100vh',
          backgroundImage: 'url("/landing-page-background.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            backgroundColor: '#1F2C40',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          p: 4
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Box
            component="img"
            src="/logo192.png"
            alt="ThinkWay"
            sx={{
              width: 120,
              height: 'auto',
              mb: 2,
              filter: 'brightness(1.2)'
            }}
          />

          {/* Mensagem dinâmica no topo */}
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 600,
              mb: 3,
              textAlign: "center",
            }}
          >
            {tabValue === 0 ? "Bem-vindo de volta!" : "Cadastre sua empresa!"}
          </Typography>

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="login tabs"
            centered
            variant="fullWidth"
            sx={{
              width: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              '& .MuiTabs-indicator': {
                height: 4,
                borderRadius: '2px',
                backgroundColor: '#ffffff'
              },
              '& .MuiTab-root': {
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '1rem',
                color: '#ffffff',
                transition: 'all 0.2s',
                '&.Mui-selected': {
                  color: '#ffffff', // força branco na tab ativa
                  fontWeight: 600,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }
            }}
          >
            <Tab label="Login" />
            <Tab label="Cadastro" />
          </Tabs>



          <TabPanel value={tabValue} index={0}>
            {error && tabValue === 0 && (
              <Typography
                sx={{
                  color: theme.palette.error.contrastText,
                  bgcolor: theme.palette.secondary.main,
                  p: 1.5,
                  borderRadius: '8px',
                  width: '100%',
                  mb: 2,
                  fontSize: '0.875rem',
                  textAlign: 'center'
                }}
              >
                {error}
              </Typography>
            )}
            
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              color='secondary'
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, handleLogin)}
              sx={{
                mb: 2,
                color: 'white',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  color: 'white',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: theme.palette.secondary.main,
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'white',
                  opacity: 1
                }
              }}
            />
            <TextField
              label="Senha"
              variant="outlined"
              type={showPassword ? "text" : "password"}  // alterna entre texto e senha
              fullWidth
              color="secondary"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, handleLogin)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: "white" }}
                      >
                        {showPassword ? (
                          <VisibilityOff sx={{ fontSize: 20, mr: 1 }} />
                        ) : (
                          <Visibility sx={{ fontSize: 20, mr: 1 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                mb: 2,
                color: "white",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  color: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.secondary.main,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                  "&.Mui-focused": {
                    color: theme.palette.secondary.main,
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "white",
                  opacity: 1,
                },
              }}
            />
            <PrimaryButton
              variant="contained"
              color="secondary"
              fullWidth
              onClick={handleLogin}
              disabled={loading}
              sx={{ py: 1 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Entrar"}
            </PrimaryButton>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Button
                onClick={handleForgotPasswordOpen}
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Esqueci minha senha
              </Button>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <RegisterForm 
              onRegister={handleRegister}
            />
          </TabPanel>
        </Box>



      </Container>
    </Container>
      <Dialog
        open={forgotPasswordOpen}
        onClose={handleForgotPasswordClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: '#1F2C40',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Recuperar senha</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(255,255,255,0.75)', mb: 2 }}>
            Informe seu e-mail de acesso. Se ele existir na base, enviaremos um link para redefinir sua senha.
          </Typography>
          {forgotPasswordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {forgotPasswordError}
            </Alert>
          )}
          {forgotPasswordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {forgotPasswordSuccess}
            </Alert>
          )}
          <TextField
            autoFocus
            label="Email"
            type="email"
            fullWidth
            value={forgotPasswordEmail}
            onChange={(e) => setForgotPasswordEmail(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, handleForgotPasswordSubmit)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                color: 'white',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleForgotPasswordClose}
            disabled={forgotPasswordLoading}
            sx={{ color: 'rgba(255, 255, 255, 0.75)' }}
          >
            Cancelar
          </Button>
          <PrimaryButton
            onClick={handleForgotPasswordSubmit}
            disabled={forgotPasswordLoading || !forgotPasswordEmail.trim()}
          >
            {forgotPasswordLoading ? <CircularProgress size={24} color="inherit" /> : "Enviar link"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoginPage;
